import {
    all, call, put, select, take, takeLatest,
} from 'redux-saga/effects';

import { proUrl } from '../../config';
import AuthenticationRoutes from '../../authentication/constants/Routes';

import { getAccessTokenSelector as getAccessTokenV1Selector } from './Authentication';

export const reducerName = 'AuthenticationV2';
const STORAGE_KEYS = {
    ACCESS_TOKEN: 'apiv2_access_token',
    REFRESH_TOKEN: 'apiv2_refresh_token',
    EXPIRATION_DATE: 'apiv2_expiration_date',
};
const REFRESH_TOKEN_THRESHOLD = 1000 * 60 * 10; // 10 minutes

/*
 * Actions
 */
const PREFIX = 'API_V2/';
export const REQUEST_POST_TOKEN = `${PREFIX}REQUEST_POST_TOKEN`;
export const REQUEST_POST_TOKEN_SUCCESS = `${PREFIX}REQUEST_POST_TOKEN_SUCCESS`;
export const REQUEST_POST_TOKEN_ERROR = `${PREFIX}REQUEST_POST_TOKEN_ERROR`;

export const REFRESH_ACCESS_TOKEN = `${PREFIX}REFRESH_ACCESS_TOKEN`;
export const SET_ACCESS_TOKEN = `${PREFIX}SET_ACCESS_TOKEN`;
export const SET_REFRESH_TOKEN = `${PREFIX}SET_REFRESH_TOKEN`;
export const SET_EXPIRATION_DATE = `${PREFIX}SET_EXPIRATION_DATE`;

/*
 * Reducer
 */
const initialState = {
    accessToken: null,
    refreshToken: null,
    clientId: YP_CONFIG.apiv2.pro.CLIENT_ID,
    clientSecret: YP_CONFIG.apiv2.pro.CLIENT_SECRET,
    expirationDate: 0,
    isLoading: false,
};

export function AuthenticationReducer(state = initialState, action) {
    switch (action.type) {
        case SET_ACCESS_TOKEN:
            return {
                ...state,
                accessToken: action.payload.accessToken,
            };
        case SET_REFRESH_TOKEN:
            return {
                ...state,
                refreshToken: action.payload.refreshToken,
            };
        case SET_EXPIRATION_DATE:
            return {
                ...state,
                expirationDate: action.payload.expirationDate,
            };
        case REQUEST_POST_TOKEN:
            return {
                ...state,
                isLoading: true,
            };
        case REQUEST_POST_TOKEN_SUCCESS:
        case REQUEST_POST_TOKEN_ERROR:
            return {
                ...state,
                isLoading: false,
            };
        default:
            return state;
    }
}

/*
 * Action Creators
 */

export function postToken(parameters) {
    return {
        type: REQUEST_POST_TOKEN,
        payload: {
            request: {
                method: 'POST',
                url: `${YP_CONFIG.apiv2.url}/token`,
                data: parameters,
            },
            extras: {
                identifier: Math.random().toString(32).slice(2),
            },
        },
    };
}

export const setAccessToken = (accessToken) => ({
    type: SET_ACCESS_TOKEN,
    payload: {
        accessToken,
    },
});

export const setRefreshToken = (refreshToken) => ({
    type: SET_REFRESH_TOKEN,
    payload: {
        refreshToken,
    },
});

export const setExpirationDate = (expirationDate) => ({
    type: SET_EXPIRATION_DATE,
    payload: {
        expirationDate,
    },
});

export const refreshAccessToken = (requestInfo) => ({
    type: REFRESH_ACCESS_TOKEN,
    payload: {
        requestWrapper: requestInfo,
    },
});

/**
 * Selectors
 */

export const getAccessTokenSelector = (state) => state[reducerName].accessToken;
export const getRefreshTokenSelector = (state) => state[reducerName].refreshToken;
export const getClientIdSelector = (state) => state[reducerName].clientId;
export const getClientSecretSelector = (state) => state[reducerName].clientSecret;
export const isLoadingSelector = (state) => state[reducerName].isLoading;

/**
 * Sagas
 */
function* callRefreshToken(action) {
    const [
        clientId,
        clientSecret,
        isLoading,
    ] = yield all([
        select(getClientIdSelector),
        select(getClientSecretSelector),
        select(isLoadingSelector),
    ]);
    if (isLoading) return;
    const refreshToken = window.localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    yield put(postToken({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: clientId,
        client_secret: clientSecret,
    }));

    const firstTryAction = yield take([
        REQUEST_POST_TOKEN_ERROR,
        REQUEST_POST_TOKEN_SUCCESS,
    ]);

    if (REQUEST_POST_TOKEN_ERROR === firstTryAction.type) {
        /* eslint-disable-next-line no-alert */
        alert('Votre session à expiré. Veuillez vous reconnecter');
        window.location = `${proUrl}${AuthenticationRoutes.SIGN_IN.link}`;
        return;
    }

    if (!(action?.payload?.requestWrapper?.extras?.actionType ?? false)) {
        return;
    }
    yield put({
        type: action.payload.requestWrapper.extras.actionType,
        payload: {
            request: action.payload.requestWrapper.request,
            extras: action.payload.requestWrapper.extras,
        },
    });
}

function cleanStorage() {
    window.localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    window.localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    window.localStorage.removeItem(STORAGE_KEYS.EXPIRATION_DATE);
}

export function* callInitAuthenticationV2() {
    const expirationDateString = window.localStorage.getItem(STORAGE_KEYS.EXPIRATION_DATE);
    const expirationDate = Number.parseInt(expirationDateString, 10);

    const accessToken = window.localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (accessToken) {
        yield put(setAccessToken(accessToken));
    }
    const refreshToken = window.localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (refreshToken) {
        yield put(setRefreshToken(refreshToken));
    }

    if (expirationDate) {
        yield put(setExpirationDate(expirationDate));
        if (expirationDate < (Date.now() + REFRESH_TOKEN_THRESHOLD)) {
            yield call(callRefreshToken);
            cleanStorage();
        }
    }

    const [
        clientId,
        clientSecret,
        accessTokenV1,
    ] = yield all([
        select(getClientIdSelector),
        select(getClientSecretSelector),
        select(getAccessTokenV1Selector),
    ]);

    if (!accessToken || !refreshToken) {
        cleanStorage();
        yield put(postToken({
            grant_type: 'api_v1',
            access_token: accessTokenV1,
            client_id: clientId,
            client_secret: clientSecret,
        }));
        const tokenAction = yield take(REQUEST_POST_TOKEN_SUCCESS);
        yield all([
            put(setAccessToken(tokenAction.payload.data.access_token)),
            put(setRefreshToken(tokenAction.payload.data.refresh_token)),
            put(setExpirationDate(Date.now() + tokenAction.payload.data.expires_in * 1000)),
        ]);
    }
}

function callSetAccessToken(action) {
    window.localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, action.payload.accessToken);
}

function callSetRefreshToken(action) {
    window.localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, action.payload.refreshToken);
}

function callSetExpirationDate(action) {
    window.localStorage.setItem(STORAGE_KEYS.EXPIRATION_DATE, action.payload.expirationDate);
}

export function* watchRefreshAccessTokenV2() {
    yield all([
        takeLatest(REFRESH_ACCESS_TOKEN, callRefreshToken),
        takeLatest(SET_ACCESS_TOKEN, callSetAccessToken),
        takeLatest(SET_REFRESH_TOKEN, callSetRefreshToken),
        takeLatest(SET_EXPIRATION_DATE, callSetExpirationDate),
    ]);
}
